import { forwardRef } from "react"
import "./Button.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { type IconProp } from "@fortawesome/fontawesome-svg-core"
import { useMediaQuery } from "react-responsive"
import { useMenuMobileContext } from "../../context/useMenuMobileContext"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { useTranslation } from "react-i18next"
import type { InteractionDescriptionProps } from "../InteractionTooltip/InteractionTooltip"

interface ButtonProps extends InteractionDescriptionProps {
  label?: string
  sizeNumber?: number
  border?: string
  bgColor?: string
  onClick?: (e: any) => void
  onFileChange?: any
  type?: "primary" | "secondary"
  size?: "small" | "medium" | "large" | "sm" | "md" | "lg" | "xs" | number
  typeButton?: "button" | "submit" | "reset"
  disabled?: boolean
  icon?: IconProp
  iconColor?: string
  animationDuration?: string
  animationTypes?: keyof typeof AnimationTypes
  ariaLabel?: string
}

const Button = forwardRef<HTMLInputElement, ButtonProps>(
  (
    {
      label,
      onClick,
      onFileChange,
      type = "primary",
      typeButton = "button",
      disabled = false,
      icon,
      bgColor,
      animationDuration,
      animationTypes = AnimationTypes.fadeInTraslate,
      descriptionKey,
      ariaLabel,
    },
    ref,
  ) => {
    const buttonClass = `button ${type} ${"small"}`

    const { isNotMobile } = useMenuMobileContext()

    const { t } = useTranslation("interactionDescriptions")

    const classButton = !isNotMobile ? "buttonDivGlobal" : ""

    return (
      <div className={classButton}>
        <Animate type={animationTypes} duration={animationDuration}>
          <div style={{ display: "inline-block", width: "100%" }} data-interaction-description-key={descriptionKey}>
            <button
              data-interaction-description-key={descriptionKey}
              type={typeButton}
              className={buttonClass}
              onClick={onClick}
              disabled={disabled}
              style={{ backgroundColor: bgColor }}
              aria-description={descriptionKey ? t(descriptionKey) : undefined}
              aria-label={ariaLabel ?? label}
              title={ariaLabel}
            >
              {icon ? <FontAwesomeIcon icon={icon} /> : label}
            </button>

            <input ref={ref} type="file" style={{ display: "none" }} onChange={onFileChange} accept=".json,.txt" />
          </div>
        </Animate>
      </div>
    )
  },
)

export default Button

export const ButtonIcon = forwardRef<HTMLInputElement, ButtonProps>(
  (
    {
      label: text,
      border = "none",
      onClick,
      sizeNumber = 60,
      disabled = false,
      bgColor: color,
      onFileChange: onFileChange,
      typeButton = "button",
      iconColor = "black",
      icon,
      animationDuration,
      animationTypes = AnimationTypes.fadeInTraslate,
      descriptionKey,
      ariaLabel,
    },
    ref,
  ) => {
    const isMobile = useMediaQuery({ query: "(max-width: 600px)" })

    const { t } = useTranslation("interactionDescriptions")

    sizeNumber = isMobile ? sizeNumber / (1 / 0.9) : sizeNumber

    if (disabled) {
      return <></>
    }

    return (
      <div className="buttonIconClass">
        <Animate type={animationTypes} duration={animationDuration}>
          <div style={{ display: "inline-block", width: "100%" }} data-interaction-description-key={descriptionKey}>
            <button
              data-interaction-description-key={descriptionKey}
              type={typeButton}
              onClick={onClick}
              disabled={disabled}
              className={"button"}
              style={{ border: border, width: sizeNumber, height: sizeNumber, borderRadius: 5, backgroundColor: color }}
              aria-label={ariaLabel ?? text ?? (descriptionKey ? t(descriptionKey) : undefined)}
              title={ariaLabel}
            >
              {icon ? (
                <FontAwesomeIcon className="icon-Button" icon={icon} style={{ color: iconColor, fontSize: 14 }} />
              ) : (
                text
              )}
            </button>

            <input ref={ref} type="file" style={{ display: "none" }} onChange={onFileChange} accept=".json,.txt" />
          </div>
        </Animate>
      </div>
    )
  },
)

const sizeMap = {
  xs: "w-8 h-8 text-xs",
  sm: "w-10 h-10 text-sm",
  md: "w-12 h-12 text-base",
  lg: "w-14 h-14 text-lg",
}

export const ButtonIconTW = forwardRef<HTMLInputElement, ButtonProps>(
  (
    {
      label,
      onClick,
      size = "md",
      disabled = false,
      bgColor = "bg-blue-500",
      iconColor = "text-white",
      icon,
      typeButton = "button",
      onFileChange,
      animationDuration = "0.4s",
      animationTypes = AnimationTypes.fadeInTraslate,
      descriptionKey,
    },
    ref,
  ) => {
    const { t } = useTranslation("interactionDescriptions")

    return (
      <Animate type={animationTypes} duration={animationDuration}>
        <div className="inline-block" data-interaction-description-key={descriptionKey}>
          <button
            data-interaction-description-key={descriptionKey}
            type={typeButton}
            onClick={onClick}
            disabled={disabled}
            className={`
              ${sizeMap[size]}
              ${bgColor}
              ${iconColor}
              rounded-xl
              flex
              items-center
              justify-center
              transition-all
              duration-200
              shadow-sm
              hover:shadow-md
              hover:scale-105
              active:scale-95
              disabled:opacity-50
              disabled:cursor-not-allowed
            `}
            aria-label={label || (descriptionKey ? t(descriptionKey) : undefined)}
          >
            {icon ? <FontAwesomeIcon icon={icon} /> : label}
          </button>

          {onFileChange && (
            <input ref={ref} type="file" className="hidden" onChange={onFileChange} accept=".json,.txt" />
          )}
        </div>
      </Animate>
    )
  },
)
